import { useEffect, useMemo, useRef, useState } from 'react';
import { Awaited, Callout, type Expected, type Step } from '@beyond-js/ui/react';
import { Condition, Describe, Usual, type Capture } from '@snapshots/domain';
import { useFrame } from '@snapshots/navigation';
import { Live, usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Outage, Problems, Who } from '@snapshots/ui';

/** How a pending snapshot is followed */
export class Wait {
	/** How often a pending snapshot is read again while the page is visible */
	static EVERY = 10_000;

	/** Past the moment a pending snapshot may still complete it has failed, and is no longer read by itself */
	static old(capture: Capture, now = Date.now()): boolean {
		return Condition.of(capture, now) === 'failed';
	}
}

/**
 * A snapshot whose packages are still arriving (decision D50, SNP-04): the family's awaited card since the
 * moment the service recorded it, with one step per package (done once its bytes are stored) and the
 * completion last. It says who is sending the packages and that stopping that sender stops it, and the
 * moment it fails unless every package has arrived; "usually about" is what completed snapshots measured
 * (`GET /v1/interface`), and "taking longer than usual" starts past their 90th percentile, never earlier than
 * `Usual.FLOOR`, with `Usual.DEFAULT` until enough were measured. The page reads the snapshot
 * again every `Wait.EVERY` while it is visible, **Check again** reads it at once, and Cancel is in the
 * header's menu. The card ends when the snapshot completes; one that failed or was cancelled is `Ended`.
 *
 * The reading is a live transport under the family session rule (`Live`): it is not sent while the
 * session is renewed or asked for, a read nobody answered is the page's to say (`onMissed`, its one line,
 * D163) only when the session is still signed after looking, and otherwise the family's dialog speaks for
 * the session. While the page's line holds the snapshot (`failing`), its schedule and its Try again read it,
 * so the card neither reads it nor offers Check again, and what it shows was read when "Last updated" says.
 */
export function Pending({ capture, onRead, failing = false, onMissed }: { capture: Capture; onRead: (capture: Capture) => void; failing?: boolean; onMissed?: (refusal: Refusal) => void }) {
	const { text, captures, language, time } = usePresence();
	const { renewal } = useFrame();
	const live = useMemo(() => new Live(renewal), [renewal]);
	const [gone, setGone] = useState(false);
	const [expected, setExpected] = useState<Expected>();
	const reading = useRef(false);
	const pending = capture.state === 'pending';

	useEffect(() => {
		let held = true;
		void captures.facts().then(({ durations }) => {
			if (!held) return;
			const { median, p90 } = Usual.of(durations);
			setExpected({ median, p90 });
		});
		return () => {
			held = false;
		};
	}, [captures]);

	const read = async (): Promise<void> => {
		if (reading.current) return;
		reading.current = true;
		try {
			onRead(await captures.get(capture.id));
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			// Not read while the session was ended: the card keeps what it shows
			if (refusal.quiet) return;
			if (refusal.code === 'NOT_FOUND') return setGone(true);
			// A failed read is Snapshots' to say only while signed; otherwise the session's dialog speaks
			if (!(await live.speaks())) {
				void live.renewed().then(() => read().catch(() => undefined));
				return;
			}
			// Nobody answered: the page's one line says so and reads it again (D163)
			if (Outage.cause(refusal)) return onMissed?.(refusal);
			throw refusal;
		} finally {
			reading.current = false;
		}
	};

	useEffect(() => {
		if (!pending || gone || failing || Wait.old(capture)) return;
		const timer = setInterval(() => {
			if (document.visibilityState === 'visible' && live.open) read().catch(() => undefined);
		}, Wait.EVERY);
		return () => clearInterval(timer);
		// The identity, the state, the page's line and the session decide the timer; `read` reaches the page through `onRead`
	}, [capture.id, pending, gone, failing, live]);

	const steps: Step[] = capture.packages.map(entry => ({ id: entry.path, label: `${entry.name} ${entry.version}`, state: entry.stored ? 'done' : 'waiting' }));
	steps.push({ id: 'complete', label: text('capture.awaited.complete'), state: pending ? 'waiting' : 'done' });
	const reason = gone
		? { text: text('capture.awaited.gone'), way: text('capture.awaited.again') }
		: pending && Wait.old(capture)
			? { text: text('capture.awaited.stalled', { missing: String(Condition.missing(capture).length), total: String(capture.packages.length) }), way: text('capture.awaited.again') }
			: null;
	const sending = pending && !Wait.old(capture) && !gone;

	return (
		<div data-awaited={capture.id} className="flex flex-col gap-2">
			<Awaited
				title={text('capture.awaited.title')}
				since={capture.created}
				expected={expected ?? null}
				steps={steps}
				reason={reason}
				check={sending && !failing ? read : null}
				ended={gone ? 'failed' : pending && !Wait.old(capture) ? null : capture.state === 'active' ? 'done' : 'failed'}
				locale={language}
			/>
			{sending && (
				<p className="text-small text-ink-muted" data-sender={Describe.sender(capture)}>
					{text(`capture.awaited.sender.${Describe.sender(capture)}`)} {text('capture.awaited.deadline', { time: time(new Date(Condition.deadline(capture)).toISOString()) })}
				</p>
			)}
		</div>
	);
}

/**
 * A snapshot that ended without completing (SNP-05): one true state, never a completion step marked done.
 * Cancelled says when and by whom; failed says how many packages never arrived; both say until when the
 * record stays and that it is made again in Packages, never restored here.
 */
export function Ended({ capture }: { capture: Capture }) {
	const { text, relative, identity, people } = usePresence();
	const { names } = useFrame();
	const standing = Condition.of(capture);
	const ended = capture.ended ?? new Date(Condition.deadline(capture)).toISOString();
	const said =
		standing === 'cancelled'
			? text('capture.ended.cancelled', { date: relative(ended), who: capture.canceller ? '{who}' : text('person.someone') })
			: text('capture.ended.failed', { missing: String(Condition.missing(capture).length), total: String(capture.packages.length), date: relative(ended) });
	const [before, after] = said.split('{who}');
	return (
		<div data-ended={standing}>
			<Callout tone={standing === 'failed' ? 'danger' : 'info'} title={text(`capture.standing.${standing}`)}>
				<p className="text-small">
					{before}
					{after !== undefined && <Who account={capture.canceller} organization={capture.organization} identity={identity} people={people} names={names} text={text} />}
					{after}
				</p>
				<p className="text-small">{text('capture.ended.kept', { until: capture.kept ? relative(capture.kept) : '—' })}</p>
			</Callout>
		</div>
	);
}
